import dayjs from "dayjs";
import classNames from "classnames";
import "./HomeSwitchOverride.css";
import {
  Row, Col, Button, Select, Space, Card, Table, Skeleton, Drawer, Segmented, DatePicker, Switch, message
} from "antd";
import {
  ReloadOutlined, LeftOutlined, RightOutlined, UserOutlined, ShopOutlined, QrcodeOutlined, MoreOutlined, CloseOutlined, MailOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/appState";
import { makeRequest } from "../../helpers/axios-manipulator";
import { useEffect, useState, useMemo } from "react";
import KpiCard from "../../components/KpiCard/KpiCard";
import FinanceIcon from '../../components/KpiCard/FinanceIcon';
// import styles from "./styles.module.css";
// Removed import of constants, now defined locally below
import { ResponsiveContainer, AreaChart, Area, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, BarChart, Bar, LabelList } from "recharts";
// import type { RangePickerProps } from "antd/es/date-picker";
// import type { User } from "../../../data/models/user";
// import type { Placards } from "../../../data/models/placards";
// import type { Artwork } from "../../../data/models/artwork";
// import type { ColumnsType } from "antd/es/table";

// Internal enum for dashboard card referencing (not shown in UI)
export enum DashboardCardKey {
  USERS = 1,
  INVENTORY = 2,
  PLACARDS = 3,
  SALES = 4,
  BUSINESS_REVENUE = 5,
  UJAMAA_REVENUE = 6,
}

const { RangePicker } = DatePicker;

// Stub for missing helper
const formatNumberToReadable = (val: any, decimals = 2) => {
  if (val === null || val === undefined || isNaN(val)) {
    return '0';
  }
  const num = Number(val);
  let result;
  if (Math.abs(num) >= 1000) {
    result = num.toLocaleString(undefined, { maximumFractionDigits: decimals });
  } else {
    result = num.toFixed(decimals);
  }
  return result;
};

// --- STUBS FOR MISSING CONSTANTS, ENUMS, AND HELPERS ---
// Colors (use Ant Design palette as placeholder)
const COLOR_1 = '#82ca9d';
const COLOR_2 = '#3b82f6';
const COLOR_3 = '#8884d8';
const COLOR_4 = '#ff7300';
  const PAGES = [
    { label: "Overview", value: 0 },
    { label: "Orders", value: 1 },
    { label: "Artists", value: 2 },
    { label: "Collectors", value: 3 }, // New item for Collectors
  ];
const INVENTORY_SALES_CHART_TYPE = {
  BOTH: { label: "Both" },
  INVENTORY: { label: "Inventory" },
  SALES: { label: "Sales" },
};
const PLACARDS_BY = {
  ARTWORK: { default: "Artwork", upperCase: "ARTWORK" },
  ARTIST: { default: "Artist", upperCase: "ARTIST" },
};
const DATE_RANGE = {
  ALL: { label: "All" },
  MONTH: { label: "Month" },
  YEAR: { label: "Year" },
  CUSTOM: { label: "Custom" },
};

// Placeholder helpers (replace with real logic as needed)
const getInventoryTotal = (data: any) => data?.inventoryCounts?.total ?? 0;
const getInventoryOriginals = (data: any) => data?.inventoryCounts?.originals ?? 0;
const getInventoryPrints = (data: any) => data?.inventoryCounts?.prints ?? 0;
const getInventoryTotalValue = (data: any) => 0;
const getInventoryOriginalsValue = (data: any) => 0;
const getInventoryPrintsValue = (data: any) => 0;
const getPlacards = (data: any) => data?.placardsCount ?? 0;
const getPlacardsScannedData = (rows: any[] = []) => {
  // Aggregate placards scanned by month/year
  if (!Array.isArray(rows)) return [];
  const grouped: Record<string, { monthYear: string; scans: number }> = {};
  rows.forEach((row: any) => {
    const date = row.created_date ? new Date(row.created_date) : null;
    if (!date) return;
    const monthYear = `${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
    if (!grouped[monthYear]) grouped[monthYear] = { monthYear, scans: 0 };
    grouped[monthYear].scans += 1;
  });
  // Sort by date ascending
  return Object.values(grouped).sort((a, b) => {
    const [am, ay] = a.monthYear.split('/').map(Number);
    const [bm, by] = b.monthYear.split('/').map(Number);
    return (ay * 12 + am) - (by * 12 + bm);
  });
};
// Real data helpers (implement as needed or import real ones)
// --- Real logic for root KPI row helpers ---
const getTotalSalesFromOrders = (orders: any[] = []) => {
  // Sum of gross sales: prefer order_amount, then fees, then artwork_price
  return orders.reduce((sum, o) => {
    let gross = 0;
    if (o.order_amount !== undefined && o.order_amount !== null && !isNaN(Number(o.order_amount))) {
      gross = Number(o.order_amount);
    } else if (o.fees !== undefined && o.fees !== null && !isNaN(Number(o.fees))) {
      gross = Number(o.fees);
    } else if (o.artwork_price !== undefined && o.artwork_price !== null && !isNaN(Number(o.artwork_price))) {
      gross = Number(o.artwork_price);
    }
    return sum + gross;
  }, 0);
};

const getTotalSalesTransactions = (orders: any[] = []) => {
  // Number of paid orders
  return orders.length;
};

const getAverageSalesFromOrders = (orders: any[] = []) => {
  if (!orders.length) return 0;
  return getTotalSalesFromOrders(orders) / orders.length;
};

const getNormalizedSalesRevenueData = (orders: any[] = [], user?: any) => {
  // Group by month/year, sum sales and revenue
  const grouped: Record<string, { sales: number; rep_revenue: number; artist_revenue: number; ujamaa_revenue: number; revenue?: number; count: number }> = {};
  // Get user and repId from closure
  // Use user from closure
  let repId = '';
  if (typeof user === 'object' && user) {
    repId = String(user.rep_id || user.id || '').trim();
  }
  const root = (user && (user.contract_id === 0 || user.contract_id === '0' || user.profile_name === 'Root'));
  orders.forEach((o) => {
    const date = new Date(o.order_date || o.order_date_est);
    const monthYear = `${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
    if (!grouped[monthYear]) grouped[monthYear] = { sales: 0, rep_revenue: 0, artist_revenue: 0, ujamaa_revenue: 0, revenue: 0, count: 0 };
    // Always use order_amount for sales if present
    grouped[monthYear].sales += Number(o.order_amount) || Number(o.fees) || Number(o.artwork_price) || 0;
    // For root users, rep_revenue should sum all rep_fees (semicolon-separated string)
    if (root) {
      if (o.rep_fees) {
        grouped[monthYear].rep_revenue += o.rep_fees.split(';').reduce((sum: number, fee: string) => sum + (parseFloat(fee) || 0), 0);
      }
    } else {
      grouped[monthYear].rep_revenue += Number(o.rep_fee_value) || 0;
    }
    grouped[monthYear].artist_revenue += Number(o.ujamaa_artist_fee) || 0;
    // ujamaa_revenue from backend (should be present)
    grouped[monthYear].ujamaa_revenue += parseFloat(String(o.ujamaa_revenue).replace(/,/g, '').trim()) || 0;
    // For non-root users, sum their rep_payout for this order
    if (!root) {
      const repIds = String(o.rep_id || '').split(';').map((id: string) => id.trim());
      const repPayouts = String(o.rep_payout || '').split(';').map((val: string) => parseFloat(val) || 0);
      const idx = repIds.indexOf(repId);
      if (idx !== -1) {
        grouped[monthYear].revenue = (grouped[monthYear].revenue || 0) + (repPayouts[idx] || 0);
      }
    }
    grouped[monthYear].count += 1;
  });
  // For root, omit 'revenue' field; for non-root, only return sales+revenue
  return Object.entries(grouped).map(([monthYear, vals]) => {
    if (root) {
      const { revenue, ...rest } = vals;
      return { monthYear, ...rest };
    } else {
      // Only return sales and revenue for non-root
      return { monthYear, sales: vals.sales, revenue: vals.revenue };
    }
  });
};

const getBusinessRevenue = (orders: any[] = []) => {
  // Sum of rep_fee_value (business revenue)
  return orders.reduce((sum, o) => sum + (Number(o.rep_fee_value) || 0), 0);
};

const getArtistRevenue = (orders: any[] = []) => {
  // Sum of ujamaa_artist_fee (artist revenue)
  return orders.reduce((sum, o) => sum + (Number(o.ujamaa_artist_fee) || 0), 0);
};

const getUjamaaRevenue = (revenue: any[] = []) => {
  // Sum of ujamaa_revenue (from revenue array)
  const sum = revenue.reduce((sum, r) => sum + (Number(r.ujamaa_revenue) || 0), 0);
  return sum;
};

const getUjamaaArtworkRevenue = (revenue: any[] = []) => {
  // Sum of ujamaa_artist_fee (from revenue array)
  return revenue.reduce((sum, r) => sum + (Number(r.ujamaa_artist_fee) || 0), 0);
};

const getUjamaaRepRevenue = (revenue: any[] = []) => {
  // Sum of ujamaa_rep_fees (from revenue array)
  return revenue.reduce((sum, r) => sum + (Number(r.ujamaa_rep_fees) || 0), 0);
};




const PlacardsDetailsTable = ({ data, placardsBy }: { data: any, placardsBy: any }) => {
    // ...logs removed...
  const [selectedRange, setSelectedRange] = useState('All Time');
  const [customDates, setCustomDates] = useState<[any, any]>([null, null]);
  if (!data || !Array.isArray(data.placardsScanned)) return null;

  // Date filtering logic (robust for Month, Week, Today)
  let filtered = data.placardsScanned;
  const now = dayjs().startOf('day');
  if (selectedRange === 'Today') {
    filtered = filtered.filter((row: any) => {
      const d = row.created_date ? dayjs(row.created_date).startOf('day') : null;
      return d && d.isSame(now, 'day');
    });
  } else if (selectedRange === 'Week') {
    filtered = filtered.filter((row: any) => {
      const d = row.created_date ? dayjs(row.created_date) : null;
      return d && d.isSame(now, 'week');
    });
  } else if (selectedRange === 'Month') {
    filtered = filtered.filter((row: any) => {
      const d = row.created_date ? dayjs(row.created_date) : null;
      return d && d.isSame(now, 'month');
    });
  } else if (selectedRange === 'Year') {
    filtered = filtered.filter((row: any) => {
      const d = row.created_date ? dayjs(row.created_date) : null;
      return d && d.isSame(now, 'year');
    });
  } else if (selectedRange === 'Custom') {
    if (customDates[0] && customDates[1]) {
      const start = dayjs(customDates[0]).startOf('day');
      const end = dayjs(customDates[1]).endOf('day');
      filtered = filtered.filter((row: any) => {
        const d = row.created_date ? dayjs(row.created_date) : null;
        return d && d.isAfter(start) && d.isBefore(end);
      });
    } else {
      filtered = [];
    }
  }


  let tableData: any[] = [];
  let columns: any[] = [];

  if (placardsBy === PLACARDS_BY.ARTIST) {
    // Group by artist
    const artistMap: Record<string, { artist: string; scans: number }> = {};
    filtered.forEach((row: any) => {
      const artist = row.display_name || row.artist_display_name || row.artist_username || 'Unknown Artist';
      if (!artistMap[artist]) {
        artistMap[artist] = { artist, scans: 0 };
      }
      artistMap[artist].scans += 1;
    });
    tableData = Object.values(artistMap).sort((a, b) => b.scans - a.scans).map((row, idx) => ({ key: idx, ...row }));
    columns = [
      { title: 'Artist', dataIndex: 'artist', key: 'artist', width: '70%' },
      { title: 'Scans', dataIndex: 'scans', key: 'scans', width: '30%' },
    ];
  } else {
    // Default: group by placard/artwork
    const placardDayMap = new Map<string, Map<string, { title: string; placard: string; date: string; count: number }>>();
    if (Array.isArray(filtered)) {
      filtered.forEach((row: any) => {
        const title = row.title || 'Untitled';
        const placard = row.placard_code || '';
        const dateStr = row.created_date ? dayjs(row.created_date).format('YYYY-MM-DD') : '';
        if (!placardDayMap.has(placard)) {
          placardDayMap.set(placard, new Map());
        }
        const dayMap = placardDayMap.get(placard)!;
        if (!dayMap.has(dateStr)) {
          dayMap.set(dateStr, { title, placard, date: dateStr, count: 1 });
        } else {
          dayMap.get(dateStr)!.count += 1;
        }
      });
    }
    const tableDataArr: { key: string; title: string; placard: string; scans: number }[] = [];
    placardDayMap.forEach((dayMap, placard) => {
      let scans = 0;
      let title = '';
      dayMap.forEach((entry) => {
        title = entry.title;
        scans += entry.count;
      });
      if (scans > 0) {
        tableDataArr.push({ key: `${title}|||${placard}`, title, placard, scans });
      }
    });
    tableData = tableDataArr.sort((a, b) => b.scans - a.scans);
    columns = [
      { title: 'Title', dataIndex: 'title', key: 'title', width: '33.33%' },
      { title: 'Placard', dataIndex: 'placard', key: 'placard', width: '33.33%' },
      { title: 'Scans', dataIndex: 'scans', key: 'scans', width: '33.33%' },
    ];
  }

  return (
    <div>
      <div style={{ marginBottom: 16, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Segmented
            options={['All Time', 'Year', 'Month', 'Week', 'Today', 'Custom']}
            value={selectedRange}
            onChange={val => setSelectedRange(val as string)}
            style={{ maxWidth: 500, margin: '0 auto' }}
          />
        </div>
        {selectedRange === 'Custom' && (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: 12 }}>
            <RangePicker
              value={customDates}
              onChange={vals => setCustomDates(vals as [any, any])}
              style={{ minWidth: 220 }}
            />
          </div>
        )}
      </div>
      <Table
        size="small"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};
const handleDateRangeSegmentChange = () => {};
const handleDateChange = () => {};

/**
 * Home dashboard view component.
 *
 * Renders the main dashboard for the business admin client, displaying KPIs, charts, and tables
 * for users, inventory, sales, placards, orders, artists, and collectors. The content and data
 * displayed are dynamically adjusted based on the user's role (root user or non-root user).
 *
 * - Root users (contract_id === 0 or '0', or profile_name === 'Root') see global data and additional KPIs,
 *   such as business revenue and Ujamaa revenue, and have access to all users, artists, and collectors.
 * - Non-root users see only their own or their organization's data, with filtered KPIs and tables.
 *
 * Features:
 * - KPI cards for users, inventory, placards, and sales.
 * - Charts for sales/revenue, inventory/sales, and placards scanned.
 * - Segmented controls for switching between revenue, inventory, and placards tables.
 * - Live polling for placards scanned table.
 * - Pagination for dashboard sections (orders, artists, collectors).
 * - Responsive layout using Ant Design components.
 *
 * @param props - Component props (any type, as this is a top-level view).
 * @returns The rendered dashboard view.
 */
function Home(props: any) {
    
    const [inventorySalesChartType, setInventorySalesChartType] = useState(INVENTORY_SALES_CHART_TYPE.BOTH.label);
    

    // Redux user
    const user = useSelector((state: RootState) => state.user.user);
    const [data, setData] = useState<any | null>(null);

    const isRootUser = () => {
      const contractId = (user as any)?.contract_id;
      return contractId === 0 || contractId === '0' || user?.profile_name === 'Root';
    };

    
    useEffect(() => {
      if (!isRootUser() && Array.isArray(data?.orders)) {
        const userObj = (data?.user || {});
        const repId = String(userObj.rep_id || userObj.id || '').trim();
        const filteredOrders = data.orders.filter((order: any) => {
          const repIds = String(order.rep_id || '').split(';').map((id: string) => id.trim());
          const status = String(order.status || '').toLowerCase();
          const paymentStatus = String(order.payment_status || '').toLowerCase();
          const match = repIds.includes(repId) && (status === 'paid' || status === 'completed' || paymentStatus === 'succeeded');
          return match;
        });
      }
    }, [data, isRootUser]);

    
    const [inventoryChartLimit, setInventoryChartLimit] = useState<'5' | '10' | 'all'>('5');
    const getLimited = (arr: any[]) => {
      if (inventoryChartLimit === 'all') return arr;
      const n = inventoryChartLimit === '10' ? 10 : 5;
      return arr.slice(0, n);
    };

    const inventorySalesChartData = useMemo(() => {
      if (!data) return [];
      // Group by artist_id, then use display_name for labels
      const artistMap: Record<string, { artist_id: string; display_name: string; inventoryValue: number; salesValue: number }> = {};
      // Inventory: group by artist_id
      if (Array.isArray(data?.inventory)) {
        data.inventory.forEach((item: any) => {
          const artist_id = String(item.artist_id);
          if (!artistMap[artist_id]) {
            artistMap[artist_id] = {
              artist_id,
              display_name: item.artist_display_name || item.display_name || item.username || 'Unknown Artist',
              inventoryValue: 0,
              salesValue: 0
            };
          }
          artistMap[artist_id].inventoryValue += Number(item.final_price) || 0;
        });
      }
      // Sales: group by artist_id
      if (Array.isArray(data?.orders)) {
        data.orders.forEach((order: any) => {
          const artist_id = String(order.artist_id);
          if (!artistMap[artist_id]) {
            artistMap[artist_id] = {
              artist_id,
              display_name: order.artist_display_name || order.display_name || order.username || 'Unknown Artist',
              inventoryValue: 0,
              salesValue: 0
            };
          }
          artistMap[artist_id].salesValue += Number(order.order_amount) || 0;
        });
      }
      // Debug: Log inventorySalesChartData for root and non-root
      if (isRootUser()) {
        // eslint-disable-next-line no-console
      } else {
        // eslint-disable-next-line no-console
      }
      // Prepare array for chart/card, using display_name for label
      let arr = Object.values(artistMap).map(a => ({
        ...a,
        label: a.display_name
      }));
      if (inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.SALES.label) {
        arr = arr.filter(a => a.salesValue > 0).sort((a, b) => b.salesValue - a.salesValue);
      } else if (inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.INVENTORY.label) {
        arr = arr.sort((a, b) => b.inventoryValue - a.inventoryValue);
      } else {
        arr = arr.sort((a, b) => (b.inventoryValue + b.salesValue) - (a.inventoryValue + a.salesValue));
      }
      return getLimited(arr);
    }, [data, isRootUser, inventoryChartLimit]);
    


    
    const showPlacardsDetailed = () => setShowPlacardsDrawer(true);
    const togglePlacardsBy = () => setPlacardsBy((prev: any) =>
      prev === PLACARDS_BY.ARTWORK ? PLACARDS_BY.ARTIST : PLACARDS_BY.ARTWORK
    );
    

    
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [showPlacardsDrawer, setShowPlacardsDrawer] = useState(false);
  const [placardsBy, setPlacardsBy] = useState(PLACARDS_BY.ARTWORK);
  const [selectedRange, setSelectedRange] = useState(DATE_RANGE.ALL.label);
  const [customDates, setCustomDates] = useState<[any, any]>([null, null]);
  const [tableType, setTableType] = useState('placards');
  // TODO: import or define real columns/styles
  const styles: any = { customTable: '' };
  const revenueColumnsRoot = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Timestamp',
      dataIndex: 'order_date',
      render: (val: any) => val ? dayjs(val).format('MMM D, YYYY h:mm:ss A') : '',
      sorter: (a: any, b: any) => dayjs(a.order_date).unix() - dayjs(b.order_date).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Artist',
      dataIndex: 'artist_username',
    },
    {
      title: 'Rep',
      dataIndex: 'rep_username',
      render: (val: any, record: any) => {
        // Prefer rep_usernames if present, else fallback to rep_username
        let reps: string[] = [];
        if (Array.isArray(record.rep_usernames) && record.rep_usernames.length > 0) {
          reps = record.rep_usernames.filter(Boolean);
        } else if (Array.isArray(record.rep_username)) {
          reps = record.rep_username.filter(Boolean);
        } else if (typeof val === 'string') {
          reps = val.split(';').filter(Boolean);
        } else if (val) {
          reps = [val];
        }
        return reps.length > 0 ? reps.join(', ') : '';
      },
    },
    {
      title: 'Placard',
      dataIndex: 'placard_code',
      sorter: (a: any, b: any) => a.placard_code.localeCompare(b.placard_code),
    },
    {
      title: 'Price',
      dataIndex: 'artwork_price',
      render: (val: any) => val ? `$${formatNumberToReadable(val)}` : '',
      sorter: (a: any, b: any) => a.artwork_price - b.artwork_price,
    },
    {
      title: 'Platform Fee',
      dataIndex: 'fees',
      sorter: (a: any, b: any) => b.fees - a.fees,
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
    },
    {
      title: 'Business Premium',
      dataIndex: 'ujamaa_rep_fees',
      render: (val: any) => {
        if (!val) return "0";
        const total = val
          .split(";")
          .map((num: string) => Number(num))
          .filter((num: number) => !isNaN(num))
          .reduce((sum: number, num: number) => sum + num, 0);
        return formatNumberToReadable(total);
      }
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Buyer',
      dataIndex: 'owner_full_name',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button 
          type="text" 
          icon={<MailOutlined />} 
          onClick={() => handleResendReceipt(record.id)}
          title="Resend Receipt"
        />
      ),
    },
  ];

  const revenueColumnsNonRoot = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Timestamp',
      dataIndex: 'order_date',
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.order_date).getTime() - new Date(b.order_date).getTime(),
      render: (text: any) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return `${date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/New_York",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        })}`;
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Artist',
      dataIndex: 'artist_username',
    },
    {
      title: 'Placard',
      dataIndex: 'placard_code',
      sorter: (a: any, b: any) => a.placard_code.localeCompare(b.placard_code),
    },
    {
      title: 'Price',
      dataIndex: 'artwork_price',
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
    },
  ];


  const inventoryColumns = [
    {
      title: 'Created On',
      dataIndex: 'created_date',
      width: 150,
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
      render: (text: any) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return `${date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/New_York",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        })}`;
      },
    },
    {
      title: 'Artwork',
      dataIndex: 'image_url',
      render: (image_url: string) => (
        <span style={{maxWidth: "120px", maxHeight: "20px"}}><img src = {image_url}  alt="Artwork" height={40}/></span>
      ),
      width: 80
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: 150,
    },
    {
      title: 'Artist',
      dataIndex: 'artist_display_name',
      width: 150,
    },
    {
      title: 'Price',
      dataIndex: 'final_price',
      width: 100,
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
      sorter: (a: any, b: any) => String(a.final_price).localeCompare(String(b.final_price)),
    },
  ];

  const inventoryColumnsNonRoot = [
    {
      title: 'Created On',
      dataIndex: 'created_date',
      width: 150,
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
      render: (text: any) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return `${date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/New_York",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        })}`;
      },
    },
    {
      title: 'Artwork',
      dataIndex: 'image_url',
      render: (image_url: string) => (
        <span style={{maxWidth: "120px", maxHeight: "20px"}}><img src = {image_url}  alt="Artwork" height={40}/></span>
      ),
      width: 80
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: 150,
    },
    {
      title: 'Artist',
      dataIndex: 'artist_display_name',
      width: 150,
    },
    {
      title: 'Price',
      dataIndex: 'final_price',
      width: 100,
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
      sorter: (a: any, b: any) => String(a.final_price).localeCompare(String(b.final_price)),
    },
  ];


  // Always return a fresh columns array to avoid AntD Table caching issues
  const getPlacardsColumnsRoot = () => ([
    {
      title: 'Timestamp',
      dataIndex: 'created_date',
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
      render: (text: any) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return `${date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/New_York",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        })}`;
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Artist',
      dataIndex: 'display_name',
    },
    {
      title: 'Rep',
      dataIndex: 'rep_username',
      render: (val: any, record: any) => {
        let raw = record.rep_usernames || record.rep_username;
        let reps: string[] = [];
        if (Array.isArray(raw)) {
          reps = raw.filter(Boolean);
        } else if (typeof raw === 'string') {
          if (raw.startsWith('{') && raw.endsWith('}')) {
             reps = raw.slice(1, -1).split(',').map(s => s.replace(/^"|"$/g, '')).filter(Boolean);
          } else {
             reps = raw.split(';').filter(Boolean);
          }
        }
        return reps.length > 0 ? reps.join(', ') : '';
      },
    },
    {
      title: 'Placard',
      dataIndex: 'placard_code',
      sorter: (a: any, b: any) => a.placard_code.localeCompare(b.placard_code),
    },
    {
      title: 'Price',
      dataIndex: 'final_price',
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
    },
  ]);

  const getPlacardsColumnsNonRoot = () => ([
    {
      title: 'Timestamp',
      dataIndex: 'created_date',
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime(),
      render: (text: any) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return `${date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/New_York",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "America/New_York",
        })}`;
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Artist',
      dataIndex: 'display_name',
    },
    {
      title: 'Rep',
      dataIndex: 'rep_username',
      render: (val: any, record: any) => {
        let raw = record.rep_usernames || record.rep_username;
        let reps: string[] = [];
        if (Array.isArray(raw)) {
          reps = raw.filter(Boolean);
        } else if (typeof raw === 'string') {
          if (raw.startsWith('{') && raw.endsWith('}')) {
             reps = raw.slice(1, -1).split(',').map(s => s.replace(/^"|"$/g, '')).filter(Boolean);
          } else {
             reps = raw.split(';').filter(Boolean);
          }
        }
        return reps.length > 0 ? reps.join(', ') : '';
      },
    },
    {
      title: 'Placard',
      dataIndex: 'placard_code',
      sorter: (a: any, b: any) => a.placard_code.localeCompare(b.placard_code),
    },
    {
      title: 'Price',
      dataIndex: 'final_price',
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '',
    },
  ]);
  const ordersColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Order Amount',
      dataIndex: 'order_amount',
      key: 'order_amount',
      render: (val: any) => val ? `$${formatNumberToReadable(val)}` : '$`0.00`',
    },
    {
      title: 'Artist Payout',
      dataIndex: 'artist_payout',
      key: 'artist_payout',
      render: (val: any) => val !== undefined && val !== null && val !== '' ? `$${formatNumberToReadable(val)}` : '$0.00',
    },
    {
      title: 'Artist',
      dataIndex: 'artist_username',
      key: 'artist_username',
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (val: any) => val ? dayjs(val).format('MMM D, YYYY h:mm:ss A') : '',
    },
    {
      title: 'Artwork',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Collector',
      key: 'collector',
      render: (row: any) => row.owner_full_name || row.collector_username || '',
    },
    {
      title: 'Collector Email',
      key: 'Collector_Email',
      render: (row: any) => row.owner_email || row.owners_email || '',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button 
          type="text" 
          icon={<MailOutlined />} 
          onClick={() => handleResendReceipt(record.id)}
          title="Resend Receipt"
        />
      ),
    },
  ];
  const allArtistsColumns = [
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Profile',
      dataIndex: 'username',
      key: 'username',
      render: (username: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.profile_image_url && (
            <img
              src={record.profile_image_url}
              alt="Profile"
              referrerPolicy="no-referrer"
              style={{ width: 40, height: 40, borderRadius: '50%' }}
            />
          )}
          <span>{username}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
  ];
  const repArtistsColumns = [
    {
      title: 'Name',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Profile',
      dataIndex: 'username',
      key: 'username',
      render: (username: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.profile_image_url && (
            <img
              src={record.profile_image_url}
              alt="Profile"
              referrerPolicy="no-referrer"
              style={{ width: 40, height: 40, borderRadius: '50%' }}
            />
          )}
          <span>{username}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
  ];

  const allCollectorsColumns = [
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Profile',
      dataIndex: 'username',
      key: 'username',
      render: (username: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.profile_image_url && (
            <img
              src={record.profile_image_url}
              alt="Profile"
              referrerPolicy="no-referrer"
              style={{ width: 40, height: 40, borderRadius: '50%' }}
            />
          )}
          <span>{username}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
  ];
  const repCollectorsColumns = [
    {
      title: 'Name',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (_: any, record: any) => {
        if (record.is_guest) {
          return record.owner_full_name || record.name || record.email || 'Guest';
        }
        return record.display_name || record.profile_name || record.username || record.email;
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (_: any, record: any) => record.is_guest ? '' : (record.location || ''),
    },
    {
      title: 'Profile Name',
      dataIndex: 'profile_name',
      key: 'profile_name',
      render: (_: any, record: any) => record.is_guest ? '' : (record.profile_name || ''),
    },
    {
      title: 'Profile',
      dataIndex: 'username',
      key: 'username',
      render: (username: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.profile_image_url && (
            <img
              src={record.profile_image_url}
              alt="Profile"
              referrerPolicy="no-referrer"
              style={{ width: 40, height: 40, borderRadius: '50%' }}
            />
          )}
          <span>{username}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
  ];

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Always use /kpiData for dashboard
        const params = new URLSearchParams();
        if (isRootUser()) {
          params.append('rootUser', 'true');
          params.append('userId', String(user.rep_id || user.id || ''));
          params.append('contract_id', String((user as any).contract_id ?? ''));
        } else {
          params.append('rootUser', 'false');
          params.append('rep_id', String(user.rep_id ?? ''));
          params.append('userId', String(user.rep_id ?? ''));
          params.append('contract_id', String((user as any).contract_id ?? ''));
        }
        const response = await makeRequest({
          origin: window.location.origin,
          method: 'get',
          url: `/kpiData?${params.toString()}`,
        });
        setData(response);
      } catch (err) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onPageSelectionChanged = (value: any) => setCurrentPage(Number(value));

  // Add state for live toggle and interval
  const [livePlacards, setLivePlacards] = useState(false);
  const [placardsTableData, setPlacardsTableData] = useState<any[]>([]);
  const [placardsLoading, setPlacardsLoading] = useState(false);

  // Fetch only placardsScanned for table refresh
  const fetchPlacardsScanned = async () => {
    setPlacardsLoading(true);
    try {
      const params = new URLSearchParams();
      if (isRootUser()) {
        params.append('rootUser', 'true');
        params.append('userId', String(user.rep_id || user.id || ''));
        params.append('contract_id', String((user as any).contract_id ?? ''));
      } else {
        params.append('rootUser', 'false');
        params.append('rep_id', String(user.rep_id ?? ''));
        params.append('userId', String(user.rep_id ?? ''));
        params.append('contract_id', String((user as any).contract_id ?? ''));
      }
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/kpiData?${params.toString()}`,
      });
      setPlacardsTableData(Array.isArray(response?.placardsScanned) ? response.placardsScanned : []);
    } catch (err) {
      setPlacardsTableData([]);
    } finally {
      setPlacardsLoading(false);
    }
  };

  const handleResendReceipt = async (orderId: string) => {
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/api/admin/resend-receipt',
        data: { orderId }
      });
      if (response.success) {
        message.success(response.message || 'Receipt resent successfully');
      } else {
        message.error(response.error || 'Failed to resend receipt');
      }
    } catch (err: any) {
      console.error('Resend receipt error:', err);
      message.error(err.response?.data?.error || 'Internal server error');
    }
  };
  // Live polling effect for Placards Reads table
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (livePlacards && tableType === 'placards') {
      fetchPlacardsScanned(); // fetch immediately
      interval = setInterval(fetchPlacardsScanned, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [livePlacards, tableType, user]);

  // When not live, sync table data to main data
  useEffect(() => {
    if (!livePlacards && Array.isArray(data?.placardsScanned)) {
      setPlacardsTableData(data.placardsScanned);
    }
  }, [livePlacards, data]);

  // --- RETURN BLOCK ---
  if (loading) return <Skeleton />;
  if (!data) return <Skeleton />;


  // Fallback UI for empty placardsScanned
  const showPlacardsEmpty = Array.isArray(data?.placardsScanned) && data.placardsScanned.length === 0;
  return (
    <div>
      {/* Top bar: Welcome and navigation */}
      <Row>
        <Col span={2}>
          <div>Welcome, <b>{user?.profile_name || user?.username || "User"}</b></div>
        </Col>
        <Col span={22} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} />
            <Button icon={<LeftOutlined />} disabled={currentPage === 0} onClick={() => onPageSelectionChanged(currentPage - 1)} />
            <Select
              style={{ width: 200, textAlign: "center" }}
              value={currentPage}
              onChange={onPageSelectionChanged}
              options={PAGES.map(page => ({ label: page.label, value: page.value }))}
            />
            <Button icon={<RightOutlined />} disabled={currentPage === PAGES.length - 1} onClick={() => onPageSelectionChanged(currentPage + 1)} />
          </Space>
        </Col>
      </Row>
      <hr />

      {/* Main dashboard content by page */}
        {currentPage === 0 ? (
          <>
            {/* KPI Row */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <KpiCard
                  labels={isRootUser() ? ["Users", "Artists", "Collectors", "Reps"] : ["Users", "Artists", "Collectors"]}
                  values={isRootUser()
                    ? [
                        Array.isArray(data?.users) ? data.users.length : 0,
                        Array.isArray(data?.users) ? data.users.filter((u: any) => u.is_artist === true || u.type === 1).length : 0,
                        Array.isArray(data?.users) ? data.users.filter((u: any) => u.is_collector === true || u.type === 2).length : 0,
                        Array.isArray(data?.users) ? data.users.filter((u: any) => Array.isArray(u.artist_rep) && u.artist_rep.length > 0).length : 0,
                      ]
                    : [
                        Array.isArray(data?.users) ? data.users.length : 0,
                        Array.isArray(data?.users) && data.users.length > 0 ? data.users.filter((u: any) => u.is_artist === true || u.type === 1).length : 0,
                        Array.isArray(data?.collectors) ? (data.collectors.length || 0) : 0,
                      ]}
                  icon={<UserOutlined style={{ fontSize: "32px", color: COLOR_2 }} />}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <KpiCard
                  labels={["Total", "Originals", "Prints"]}
                  values={[
                    getInventoryTotal(data),
                    getInventoryOriginals(data) || "0",
                    getInventoryPrints(data) || "0",
                  ]}
                  secondaryValues={[
                    getInventoryTotalValue(data),
                    getInventoryOriginalsValue(data),
                    getInventoryPrintsValue(data),
                  ]}
                  icon={<ShopOutlined style={{ fontSize: "32px", color: COLOR_4 }} />} 
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <KpiCard
                  labels={["Placards", "Scanned"]}
                  values={[
                    getPlacards(data),
                    `${data?.scansCount ?? 0} times`,
                  ]}
                  icon={<QrcodeOutlined style={{ fontSize: "32px", color: COLOR_3 }} />}
                />
              </Col>
            </Row>

            {/* Extra KPI Row (always visible) */}
            <Row gutter={16}>
                {isRootUser() && (
                  <Col xs={24} sm={12} md={8} style={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <KpiCard
                      labels={
                        isRootUser()
                          ? ["Gross Sales", "Orders", "Avg Sale"]
                          : ["Gross Sales", "Profit", "Orders", "Avg Sale"]
                      }
                      values={
                        (() => {
                          if (isRootUser()) {
                            return [
                              formatNumberToReadable(getTotalSalesFromOrders(data?.orders)),
                              getTotalSalesTransactions(data?.orders),
                              formatNumberToReadable(getAverageSalesFromOrders(data?.orders)),
                            ];
                          } else {
                            // Debug: Print orders and user rep_id
                            // eslint-disable-next-line no-console
                            const user = (data?.user || {});
                            const repId = String(user.rep_id || user.id || "").trim();
                            // eslint-disable-next-line no-console
                            // Filter orders for which this rep_id is present and status is paid/succeeded
                            const filteredOrders = (Array.isArray(data?.orders) ? data.orders : []).filter((order: any) => {
                              const repIds = String(order.rep_id || '').split(';').map(id => id.trim());
                              const status = String(order.status || '').toLowerCase();
                              const paymentStatus = String(order.payment_status || '').toLowerCase();
                              // Debug: Show repIds and match result
                              // eslint-disable-next-line no-console
                              return repIds.includes(repId) && (status === 'paid' || status === 'completed' || paymentStatus === 'succeeded');
                            });
                            // eslint-disable-next-line no-console

                            // Debug: Profit calculation for non-root users
                            let totalProfit = 0;
                            filteredOrders.forEach((order: any) => {
                              const repIds = String(order.rep_id || '').split(';').map(id => id.trim());
                              const repPayouts = String(order.rep_payout || '').split(';').map(val => parseFloat(val) || 0);
                              const idx = repIds.indexOf(repId);
                              const profit = idx !== -1 ? repPayouts[idx] || 0 : 0;
                              totalProfit += profit;
                              // eslint-disable-next-line no-console
                            });
                            // eslint-disable-next-line no-console
                            let gross = 0;
                            let payout = 0;
                            filteredOrders.forEach((order: any) => {
                              const repIds = String(order.rep_id || '').split(';').map(id => id.trim());
                              const repPayouts = String(order.rep_payout || '').split(';').map(val => parseFloat(val) || 0);
                              const idx = repIds.indexOf(repId);
                              if (idx !== -1) {
                                payout += repPayouts[idx] || 0;
                              }
                            });
                            return [
                              formatNumberToReadable(getTotalSalesFromOrders(filteredOrders)),
                              formatNumberToReadable(payout),
                              filteredOrders.length,
                              formatNumberToReadable(getAverageSalesFromOrders(filteredOrders)),
                            ];
                          }
                        })()
                      }
                      icon={<FinanceIcon style={{ fontSize: 28, color: '#52c41a' }} />}
                      // No chart for Gross Sales KPI card
                    />
                  </Col>
                )}
                {isRootUser() && (
                  <Col xs={24} sm={12} md={8}>
                    <KpiCard
                      labels={["Ujamaa Revenue", "Artwork Fee", "Rep Fee"]}
                      values={[
                        formatNumberToReadable(getUjamaaRevenue(data?.revenue) || 0),
                        formatNumberToReadable(getUjamaaArtworkRevenue(data?.revenue) || 0),
                        formatNumberToReadable(getUjamaaRepRevenue(data?.revenue) || 0),
                      ]}
                      chart={
                        showPlacardsEmpty ? (
                          <div style={{ textAlign: 'center', color: '#888', padding: '16px' }}>
                            No placard reads available.
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={50}>
                            <AreaChart
                              data={getPlacardsScannedData(data?.placardsScanned)}
                              margin={{ top: 5, right: 0, left: 0, bottom: 0 }}
                            >
                              <Area type="monotone" dataKey={"scans"} stroke={COLOR_3} fill={COLOR_3} />
                            </AreaChart>
                          </ResponsiveContainer>
                        )
                      }
                    />
                  </Col>
                )}
              </Row>

            {/* Charts Row */}
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Card title="Sales/Revenue" style={{ height: 320, display: 'flex', flexDirection: 'column' }}>
                  {(() => {
                    // For root user, merge ujamaa_revenue from data.revenue into chart data
                    let salesChartData = getNormalizedSalesRevenueData(data?.orders, user);
                    if (isRootUser()) {
                      // Merge ujamaa_revenue from data.revenue
                      if (Array.isArray(data?.revenue)) {
                        const ujamaaRevenueByMonth: { [key: string]: number } = {};
                        data.revenue.forEach((r: any) => {
                          const date = new Date(r.order_date_est || r.order_date);
                          const monthYear = `${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
                          ujamaaRevenueByMonth[monthYear] = (ujamaaRevenueByMonth[monthYear] || 0) + parseFloat(r.ujamaa_revenue || 0);
                        });
                        salesChartData = salesChartData.map((row: any) => ({
                          ...row,
                          ujamaa_revenue: ujamaaRevenueByMonth[row.monthYear] || 0,
                        }));
                      }

                      // Aggregate artist_revenue from artist_payout in orders
                      if (Array.isArray(data?.orders)) {
                        // Build a map of artist_payout by monthYear
                        const artistRevenueByMonth: { [key: string]: number } = {};
                        data.orders.forEach((order: any) => {
                          if (!order.artist_payout) return;
                          // Parse order date
                          const date = new Date(order.order_date_est || order.order_date);
                          const monthYear = `${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
                          const payout = parseFloat(order.artist_payout) || 0;
                          artistRevenueByMonth[monthYear] = (artistRevenueByMonth[monthYear] || 0) + payout;
                        });
                        salesChartData = salesChartData.map((row: any) => ({
                          ...row,
                          artist_revenue: artistRevenueByMonth[row.monthYear] || 0,
                        }));
                      }
                    }
                    if (!isRootUser()) {
                      // eslint-disable-next-line no-console
                   }
                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart
                          data={salesChartData}
                          margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                        >
                          <CartesianGrid stroke="#f5f5f5" />
                          <XAxis
                            dataKey="monthYear"
                            interval={"preserveStartEnd"}
                            tickFormatter={(tick) => {
                              const [month, year] = tick.split("/");
                              const date = new Date(Number(year) + 2000, Number(month) - 1);
                              return date.toLocaleString("en-US", { month: "short" });
                            }}
                          />
                          <Tooltip formatter={(value) => formatNumberToReadable(value)} contentStyle={{ opacity: 0.9 }} />
                          <Legend />
                          <Area type="monotone" dataKey="sales" fill={COLOR_2} stroke={COLOR_2} activeDot={{ r: 5 }} />
                          <Line type="monotone" dataKey={isRootUser() ? "rep_revenue" : "revenue"} stroke={COLOR_1} activeDot={{ r: 6 }} />
                          {isRootUser() ? (
                            <>
                              <Line type="monotone" dataKey="artist_revenue" stroke={COLOR_4} activeDot={{ r: 7 }} />
                              <Line type="monotone" dataKey="ujamaa_revenue" stroke={COLOR_3} activeDot={{ r: 8 }} />
                            </>
                          ) : null}
                        </ComposedChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card title="Inventory/Sales" style={{ height: 320, display: 'flex', flexDirection: 'column' }}>
                  {/* Filters row below title, centered */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                      <Segmented
                        size={"small"}
                        options={Object.values(INVENTORY_SALES_CHART_TYPE).map(({ label }) => label)}
                        value={inventorySalesChartType}
                        onChange={(value) => setInventorySalesChartType(String(value))}
                        style={{ minWidth: 180 }}
                      />
                    </div>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Segmented
                        size={"small"}
                        options={[
                          { label: 'Top 5', value: '5' },
                          { label: 'Top 10', value: '10' },
                          { label: 'All', value: 'all' },
                        ]}
                        value={inventoryChartLimit}
                        onChange={val => setInventoryChartLimit(val as '5' | '10' | 'all')}
                        style={{ minWidth: 180 }}
                      />
                    </div>
                  </div>
                  {Array.isArray(inventorySalesChartData) && inventorySalesChartData.length > 0 ? (
                    (() => {
                      const rowHeight = 50;
                      const minRows = 1;
                      const maxRows = 5;
                      const rows = Math.max(minRows, Math.min(maxRows, inventorySalesChartData.length));
                      const chartHeight = rowHeight * rows + 40; // 40px for axes/legend padding
                      return (
                        <div style={{ width: "100%", border: "1px solid #ddd", paddingRight: "10px", height: 220 }}>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart
                              data={inventorySalesChartData}
                              layout="vertical"
                              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
                            >
                              <CartesianGrid stroke="#f5f5f5" />
                              <XAxis type="number" tickFormatter={(val) => formatNumberToReadable(val)} />
                              <YAxis dataKey="label" type="category" width={120} />
                              <Tooltip formatter={(val) => formatNumberToReadable(val)} />
                              <Legend />
                              {(inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.BOTH.label ||
                                inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.INVENTORY.label) && (
                                <Bar dataKey="inventoryValue" stackId="a" fill={COLOR_4} name="Inventory" />
                              )}
                              {(inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.BOTH.label ||
                                inventorySalesChartType === INVENTORY_SALES_CHART_TYPE.SALES.label) && (
                                <Bar dataKey="salesValue" stackId="a" fill={COLOR_1} name="Sales" />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })()
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                      No artist inventory data available.
                    </div>
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card
                  title="Placards/Scanned"
                  style={{ height: 320, display: 'flex', flexDirection: 'column' }}
                  extra={
                    <Button
                      shape={"circle"}
                      icon={<MoreOutlined />}
                      style={{ border: "none" }}
                      onClick={showPlacardsDetailed}
                    />
                  }
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={getPlacardsScannedData(data?.placardsScanned)}>
                      <XAxis
                        dataKey="monthYear"
                        tickFormatter={(tick) => {
                          const [month, year] = tick.split("/");
                          const date = new Date(Number(year) + 2000, Number(month) - 1);
                          return date.toLocaleString("en-US", { month: "short" });
                        }}
                      />
                      <Tooltip />
                      <Bar dataKey="scans" fill={COLOR_3} maxBarSize={50}>
                        <LabelList
                          dataKey="scans"
                          fill={"#f0a000"}
                          fontSize={10}
                          content={({ x, y, value, height, width }: any) => {
                            const minHeight = 15;
                            const h = typeof height === "number" ? height : 0;
                            const w = typeof width === "number" ? width : 0;
                            const xx = typeof x === "number" ? x : 0;
                            const yy = typeof y === "number" ? y : 0;
                            const isTooShort = h < minHeight;
                            return (
                              <text
                                x={xx + w / 2}
                                y={isTooShort ? yy - 5 : yy + h / 2}
                                fill={isTooShort ? "black" : "white"}
                                fontSize={12}
                                textAnchor="middle"
                                dominantBaseline="middle"
                              >
                                {value}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Drawer
                    placement={"right"}
                    size="large"
                    closable={true}
                    onClose={() => setShowPlacardsDrawer(false)}
                    open={showPlacardsDrawer}
                    title={
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Space>
                          <span style={{ fontSize: "18px", fontWeight: "bold" }}>Placards Scanned - </span>
                          <span
                            style={{
                              fontSize: "16px",
                              fontWeight: "bolder",
                              color: placardsBy === PLACARDS_BY.ARTWORK ? COLOR_4 : COLOR_2,
                            }}
                          >
                            {placardsBy.upperCase}
                          </span>
                        </Space>
                        <Button
                          icon={<CloseOutlined />}
                          style={{ border: "none" }}
                          shape={"circle"}
                          onClick={() => setShowPlacardsDrawer(false)}
                        />
                      </div>
                    }
                    footer={
                      <Button
                        type="primary"
                        onClick={togglePlacardsBy}
                        ghost
                        style={{
                          width: "100%",
                          borderColor: placardsBy === PLACARDS_BY.ARTIST ? COLOR_4 : COLOR_2,
                          color: placardsBy === PLACARDS_BY.ARTIST ? COLOR_4 : COLOR_2,
                        }}
                      >
                        View by {placardsBy === PLACARDS_BY.ARTIST ? PLACARDS_BY.ARTWORK.default : PLACARDS_BY.ARTIST.default}
                      </Button>
                    }
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {/* Date filter controls are now handled inside PlacardsDetailsTable below */}
                    </div>
                    <br />
                    <PlacardsDetailsTable data={data} placardsBy={placardsBy} />
                  </Drawer>
                </Card>
              </Col>
            </Row>

            {/* Segmented Control for Revenue, Inventory, Placards Tables */}
            <Row gutter={16}>
              <Col span={24}>
                <div style={{ width: "100%", marginTop: "0.5rem" }}>
                  <Segmented
                    size="large"
                    block
                    options={[
                      { label: (<><span role="img" aria-label="dollar" className="anticon anticon-dollar"><svg viewBox="64 64 896 896" focusable="false" data-icon="dollar" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372zm47.7-395.2l-25.4-5.9V348.6c38 5.2 61.5 29 65.5 58.2.5 4 3.9 6.9 7.9 6.9h44.9c4.7 0 8.4-4.1 8-8.8-6.1-62.3-57.4-102.3-125.9-109.2V263c0-4.4-3.6-8-8-8h-28.1c-4.4 0-8 3.6-8 8v33c-70.8 6.9-126.2 46-126.2 119 0 67.6 49.8 100.2 102.1 112.7l24.7 6.3v142.7c-44.2-5.9-69-29.5-74.1-61.3-.6-3.8-4-6.6-7.9-6.6H363c-4.7 0-8.4 4-8 8.7 4.5 55 46.2 105.6 135.2 112.1V761c0 4.4 3.6 8 8 8h28.4c4.4 0 8-3.6 8-8.1l-.2-31.7c78.3-6.9 134.3-48.8 134.3-124-.1-69.4-44.2-100.4-109-116.4zm-68.6-16.2c-5.6-1.6-10.3-3.1-15-5-33.8-12.2-49.5-31.9-49.5-57.3 0-36.3 27.5-57 64.5-61.7v124zM534.3 677V543.3c3.1.9 5.9 1.6 8.8 2.2 47.3 14.4 63.2 34.4 63.2 65.1 0 39.1-29.4 62.6-72 66.4z"></path></svg></span> Revenue</>), value: 'revenue' },
                      { label: (<><span role="img" aria-label="shop" className="anticon anticon-shop"><svg viewBox="64 64 896 896" focusable="false" data-icon="shop" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M882 272.1V144c0-17.7-14.3-32-32-32H174c-17.7 0-32 14.3-32 32v128.1c-16.7 1-30 14.9-30 31.9v131.7a177 177 0 0014.4 70.4c4.3 10.2 9.6 19.8 15.6 28.9v345c0 17.6 14.3 32 32 32h676c17.7 0 32-14.3 32-32V535a175 175 0 0015.6-28.9c9.5-22.3 14.4-46 14.4-70.4V304c0-17-13.3-30.9-30-31.9zM214 184h596v88H214v-88zm362 656.1H448V736h128v104.1zm234 0H640V704c0-17.7-14.3-32-32-32H416c-17.7 0-32 14.3-32 32v136.1H214V597.9c2.9 1.4 5.9 2.8 9 4 22.3 9.4 46 14.1 70.4 14.1s48-4.7 70.4-14.1c13.8-5.8 26.8-13.2 38.7-22.1.2-.1.4-.1.6 0a180.4 180.4 0 0038.7 22.1c22.3 9.4 46 14.1 70.4 14.1 24.4 0 48-4.7 70.4-14.1 13.8-5.8 26.8-13.2 38.7-22.1.2-.1.4-.1.6 0a180.4 180.4 0 0038.7 22.1c22.3 9.4 46 14.1 70.4 14.1 24.4 0 48-4.7 70.4-14.1 3-1.3 6-2.6 9-4v242.2zm30-404.4c0 59.8-49 108.3-109.3 108.3-40.8 0-76.4-22.1-95.2-54.9-2.9-5-8.1-8.1-13.9-8.1h-.6c-5.7 0-11 3.1-13.9 8.1A109.24 109.24 0 01512 544c-40.7 0-76.2-22-95-54.7-3-5.1-8.4-8.3-14.3-8.3s-11.4 3.2-14.3 8.3a109.63 109.63 0 01-95.1 54.7C233 544 184 495.5 184 435.7v-91.2c0-.3.2-.5.5-.5h655c.3 0 .5.2.5.5v91.2z"></path></svg></span> Inventory</>), value: 'inventory' },
                      { label: (<><span role="img" aria-label="qrcode" className="anticon anticon-qrcode"><svg viewBox="64 64 896 896" focusable="false" data-icon="qrcode" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M468 128H160c-17.7 0-32 14.3-32 32v308c0 4.4 3.6 8 8 8h332c4.4 0 8-3.6 8-8V136c0-4.4-3.6-8-8-8zm-56 284H192V192h220v220zm-138-74h56c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm194 210H136c-4.4 0-8 3.6-8 8v308c0 17.7 14.3 32 32 32h308c4.4 0 8-3.6 8-8V556c0-4.4-3.6-8-8-8zm-56 284H192V612h220v220zm-138-74h56c4.4 0 8-3.6 8-8v-56c0-4.4 3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm590-630H556c-4.4 0-8 3.6-8 8v332c0 4.4 3.6 8 8 8h332c4.4 0 8-3.6 8-8V160c0-17.7-14.3-32-32-32zm-32 284H612V192h220v220zm-138-74h56c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm194 210h-48c-4.4 0-8 3.6-8 8v134h-78V556c0-4.4-3.6-8-8-8H556c-4.4 0-8 3.6-8 8v332c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V644h78v102c0 4.4 3.6 8 8 8h190c4.4 0 8-3.6 8-8V556c0-4.4-3.6-8-8-8zM746 832h-48c-4.4 0-8 3.6-8 8v48c0 4.4 0 8 8 8h48c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zm142 0h-48c-4.4 0-8 3.6-8 8v48c0 4.4 0 8 8 8h48c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8z"></path></svg></span> Placards Reads</>), value: 'placards' },
                    ]}
                    value={tableType}
                    onChange={(val: string | number) => setTableType(val as string)}
                  />
                  <Card style={{ marginTop: '1rem' }}
                    extra={tableType === 'placards' && (
                      <Switch
                        checked={livePlacards}
                        onChange={setLivePlacards}
                        checkedChildren="Live"
                        unCheckedChildren="Live"
                        className={classNames({ 'live-red-switch': livePlacards })}
                        style={{ marginLeft: 8 }}
                      />
                    )}
                  >
                    <Table
                      key={tableType}
                      size='small'
                      className={styles.customTable}
                      rowKey={record => record.id?.toString()}
                      columns={
                        tableType === 'revenue' ? (isRootUser() ? revenueColumnsRoot : revenueColumnsNonRoot) :
                        tableType === 'inventory' ? (isRootUser() ? inventoryColumns : inventoryColumnsNonRoot) :
                        (isRootUser() ? getPlacardsColumnsRoot() : getPlacardsColumnsNonRoot())
                      }
                      dataSource={
                        tableType === 'placards' ? placardsTableData :
                        tableType === 'revenue' ? data?.orders :
                        tableType === 'inventory' ? (isRootUser() ?
                          (Array.isArray(data?.inventory)
                            ? data.inventory
                            : [])
                          :
                          ([
                            ...(
                              Array.isArray(data?.dealerOriginals)
                                ? data.dealerOriginals
                                : (Array.isArray(data?.dealerOriginals?.rows) ? data.dealerOriginals.rows : [])
                            ),
                            ...(
                              Array.isArray(data?.dealerPrints)
                                ? data.dealerPrints
                                : (Array.isArray(data?.dealerPrints?.rows) ? data.dealerPrints.rows : [])
                            )
                          ].filter((row: any, idx: number, arr: any[]) =>
                            arr.findIndex(r => r.id === row.id) === idx
                          ))) :
                        []
                      }
                      loading={tableType === 'placards' ? placardsLoading : false}
                      scroll={{ y: 400 }}
                    />
                  </Card>
                </div>
              </Col>
            </Row>
          </>
        ) : currentPage === 1 ? (
          <Card title="Orders" extra={`${data?.orders?.length} Found`}>
            <Table
              size='small'
              className={styles.customTable}
              rowKey="id"
              columns={ordersColumns}
              dataSource={data?.orders}
              scroll={{ y: 1200 }}
            />
          </Card>
        ) : currentPage === 2 ? (
          <Card title="Artists" extra={`${(isRootUser() ? data?.users?.filter((artist: any) => artist.is_artist === "1") : data?.artists)?.length} Found`}>
            <Table
              size='small'
              className={styles.customTable}
              rowKey="id"
              columns={isRootUser() ? allArtistsColumns : repArtistsColumns}
              dataSource={isRootUser() ? data?.users?.filter((artist: any) => artist.is_artist === "1") : data?.artists}
              scroll={{ y: 1200 }}
            />
          </Card>
        ) : currentPage === 3 ? (
          <Card title="Collectors" extra={`${(isRootUser() ? data?.users?.filter((user: any) => user.type === 2) : data?.collectors)?.length} Found`}>
            <Table
              size='small'
              className={styles.customTable}
              rowKey="id"
              columns={isRootUser() ? allCollectorsColumns : repCollectorsColumns}
              dataSource={isRootUser() ? data?.users?.filter((user: any) => user.type === 2) : data?.collectors} // Assuming 'collectors' key for non-root
              scroll={{ y: 1200 }}
            />
          </Card>
        ) : null}
      </div>
  );
}

export default Home;